import { useCallback, useEffect, useState } from 'react';
import { formatAtomic } from '@urbanrats/domain';
import type { ClientGateways, DropPrize, EngagementView } from '../gateway/index.ts';
import { useI18n, type MessageKey } from '../i18n/index.ts';
import { fmtDateTime, localiseDecimal } from '../lib/format.ts';

/** Server view of leaderboards and the drop chest, refreshed every `everyMs` (null when not available). */
export function useEngagement(gw: ClientGateways, everyMs = 15_000) {
  const [view, setView] = useState<EngagementView | null>(null);
  const reload = useCallback(async () => {
    const v = await gw.engagement?.get();
    if (v) setView(v);
  }, [gw]);
  useEffect(() => {
    if (!gw.engagement) return;
    const first = setTimeout(() => void reload(), 0);
    const t = setInterval(() => void reload(), everyMs);
    return () => (clearTimeout(first), clearInterval(t));
  }, [gw, reload, everyMs]);
  return { view, reload };
}

const flag = (cc: string | null) => (cc && /^[A-Z]{2}$/.test(cc) ? String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : '🌐');

function useFormat(gw: ClientGateways) {
  const { t, intl } = useI18n();
  const money = (atomic: string, currency: string | null) => `${localiseDecimal(formatAtomic(BigInt(atomic), currency === gw.currency.code ? gw.currency.scale : 2), intl)} ${currency ?? ''}`.trim();
  const metricValue = (metric: string, value: string, currency: string | null) => {
    if (metric === 'PLAY_TIME') {
      const s = Number(value);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      return h ? t('ranks.hm', { h, m }) : t('ranks.m', { m: Math.max(m, s > 0 ? 1 : 0) });
    }
    if (metric === 'BEST_MULTIPLIER') return `×${localiseDecimal((Number(value) / 10_000).toFixed(2), intl)}`;
    if (metric === 'STAKE' || metric === 'NET_WIN' || metric === 'BIGGEST_WIN') return money(value, currency);
    return localiseDecimal(value, intl);
  };
  const prize = (p: DropPrize) => (p.kind === 'FREEBET' && p.value ? t('prize.freebet', { count: p.count, amount: money(p.value, p.currency ?? gw.currency.code) }) : t('prize.boost', { pct: (p.boostBps ?? 0) / 100 }));
  return { t, intl, metricValue, prize };
}

/** Leaderboards the player's site shows: top 20, the player's own rank, the time left and the prizes. */
export function RanksPanel({ gw }: { gw: ClientGateways }) {
  const { view } = useEngagement(gw, 10_000);
  const { t, intl, metricValue, prize } = useFormat(gw);
  const [pick, setPick] = useState(0);
  if (!view) return <p className="note">{t('ranks.loading')}</p>;
  if (!view.features.leaderboards || view.boards.length === 0) return <p className="note" data-testid="ranks-empty">{t('ranks.empty')}</p>;
  const b = view.boards[Math.min(pick, view.boards.length - 1)]!;
  return (
    <div className="ranks" data-testid="ranks-panel">
      {view.boards.length > 1 && (
        <div className="ranks-tabs" role="tablist">
          {view.boards.map((x, i) => (
            <button key={x.boardId} type="button" role="tab" aria-selected={i === pick} className={i === pick ? 'on' : ''} onClick={() => setPick(i)}>
              {x.name}
            </button>
          ))}
        </div>
      )}
      <div className="ranks-head">
        <div>
          <strong>{b.name}</strong>
          <span className="note">
            {t(`metric.${b.metric}` as MessageKey)} · {t('ranks.ends', { time: fmtDateTime(b.endsAt, intl) })}
          </span>
        </div>
        <div className="ranks-me" data-testid="ranks-me">
          {b.me ? (
            <>
              <span className="note">{t('ranks.yourRank')}</span>
              <b>#{b.me.rank}</b>
              <span>{metricValue(b.metric, b.me.value, b.currency)}</span>
            </>
          ) : (
            <span className="note">{t('ranks.notRanked', { n: b.minRounds })}</span>
          )}
        </div>
      </div>
      <ol className="ranks-list">
        {b.top.map((r) => (
          <li key={r.rank} className={`${r.me ? 'me' : ''} ${r.rank <= 3 ? `top${r.rank}` : ''}`}>
            <span className="rk">{r.rank <= 3 ? ['🥇', '🥈', '🥉'][r.rank - 1] : r.rank}</span>
            <span className="who">
              <span aria-hidden>{flag(r.country)}</span> {r.alias}
              {r.me && <em> · {t('ranks.you')}</em>}
            </span>
            <span className="val">{metricValue(b.metric, r.value, b.currency)}</span>
          </li>
        ))}
      </ol>
      {b.prizes.length > 0 && (
        <div className="ranks-prizes">
          <h4>{t('ranks.prizes')}</h4>
          <ul>
            {b.prizes.map((p) => (
              <li key={p.rankFrom}>
                <span>{p.rankFrom === p.rankTo ? `#${p.rankFrom}` : `#${p.rankFrom}–${p.rankTo}`}</span>
                <span>{prize({ kind: p.kind, count: p.count, value: p.value, boostBps: p.boostBps })}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {view.alias && <p className="note">{t('ranks.alias', { alias: view.alias })}</p>}
    </div>
  );
}

const left = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m ${String(s % 60).padStart(2, '0')}s`;
};

/** The drop chest on the game screen: countdown, progress towards the target, open once when reached. */
export function DropChest({ gw, nowMs }: { gw: ClientGateways; nowMs: number }) {
  const { view, reload } = useEngagement(gw, 15_000);
  const { t, intl, prize } = useFormat(gw);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [won, setWon] = useState<DropPrize | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const d = view?.features.drops ? view.drop : null;
  if (!d) return null;
  const pct = Math.min(100, Math.round((d.progress / d.target) * 100));
  const target = d.metric === 'BEST_MULTIPLIER' ? `×${localiseDecimal((d.target / 100).toFixed(2), intl)}` : String(d.target);
  const progress = d.metric === 'BEST_MULTIPLIER' ? `×${localiseDecimal((d.progress / 100).toFixed(2), intl)}` : String(d.progress);
  const ready = d.canOpen && !won;
  return (
    <div className={`chest ${ready ? 'ready' : ''} ${open ? 'expanded' : ''}`} data-testid="drop-chest">
      <button type="button" className="chest-btn" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-label={t('drop.title')}>
        <span className="chest-icon" aria-hidden>
          {d.opened || won ? '🎁' : '🧰'}
        </span>
        <span className="chest-mini">
          <b>{left(new Date(d.endsAt).getTime() - nowMs)}</b>
          <span className="chest-bar">
            <i style={{ width: `${pct}%` }} />
          </span>
        </span>
      </button>
      {open && (
        <div className="chest-card" role="dialog" aria-label={d.name}>
          <strong>{d.name}</strong>
          <p>{t('drop.goal', { metric: t(`dropMetric.${d.metric}` as MessageKey, { target }) })}</p>
          <div className="chest-progress" aria-label={t('drop.progress', { progress, target })}>
            <span className="chest-bar big">
              <i style={{ width: `${pct}%` }} />
            </span>
            <span>
              {progress} / {target}
            </span>
          </div>
          <p className="note">
            {t('drop.ends', { left: left(new Date(d.endsAt).getTime() - nowMs) })} · {d.opens < d.maxOpens ? t('drop.left', { n: d.maxOpens - d.opens }) : t('drop.empty')}
          </p>
          {won || d.opened ? (
            <p className="chest-won" data-testid="drop-won">
              {t('drop.won', { prize: prize(won ?? d.opened!.prize) })}
            </p>
          ) : (
            <button
              type="button"
              className="confirm"
              disabled={!ready || busy}
              data-testid="drop-open"
              onClick={async () => {
                setBusy(true);
                setErr(null);
                const r = await gw.engagement!.openDrop(d.dropId);
                setBusy(false);
                if (r.ok) setWon(r.prize);
                else setErr(r.error);
                await reload();
              }}
            >
              {t('drop.open')}
            </button>
          )}
          {err && <p className="note">{err}</p>}
          <details className="chest-fair">
            <summary>{t('drop.fairTitle')}</summary>
            <p className="note">{t('drop.fair')}</p>
            <code>{d.seedCommitment}</code>
            {d.seed && <code>seed {d.seed}</code>}
          </details>
        </div>
      )}
    </div>
  );
}
